import { Wifi, WifiOff, RefreshCw, Check, AlertCircle, ShieldCheck, ShieldAlert, ShieldX, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useLicenseStore } from '@/stores/licenseStore';
import { useSyncStore } from '@/stores/syncStore';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { Link } from '@tanstack/react-router';

/**
 * Compact status indicators for the header bar
 * Shows network, sync, and license status in a minimal format
 * Clickable license badge routes to subscription page
 */
export function StatusIndicators() {
  const isOnline = useNetworkStatus();
  const { syncState } = useSyncStore();
  const { licenseState, daysUntilExpiry: getDaysUntilExpiry } = useLicenseStore();
  const daysUntilExpiry = typeof getDaysUntilExpiry === 'function' ? getDaysUntilExpiry() : getDaysUntilExpiry;

  // Network status
  const networkConfig = isOnline
    ? { icon: Wifi, color: 'text-green-500', label: 'En ligne', bgColor: 'bg-green-500' }
    : { icon: WifiOff, color: 'text-red-500', label: 'Hors ligne', bgColor: 'bg-red-500' };

  // Sync status
  const getSyncConfig = () => {
    switch (syncState) {
      case 'syncing':
        return { icon: RefreshCw, color: 'text-blue-500', label: 'Synchronisation...', animate: true };
      case 'synced':
        return { icon: Check, color: 'text-green-500', label: 'Synchronisé', animate: false };
      case 'pending':
        return { icon: RefreshCw, color: 'text-amber-500', label: 'En attente', animate: false };
      case 'error':
        return { icon: AlertCircle, color: 'text-red-500', label: 'Erreur sync', animate: false };
      default:
        return { icon: Check, color: 'text-gray-500', label: 'Prêt', animate: false };
    }
  };

  // License status
  const getLicenseConfig = () => {
    switch (licenseState) {
      case 'valid':
        return { icon: ShieldCheck, color: 'text-green-500', label: 'Licence valide', bgColor: 'bg-green-500/10' };
      case 'verification_needed':
        return { icon: ShieldAlert, color: 'text-amber-500', label: 'Vérification requise', bgColor: 'bg-amber-500/10' };
      case 'expired':
        return { icon: ShieldX, color: 'text-red-500', label: 'Licence expirée', bgColor: 'bg-red-500/10' };
      case 'verifying':
        return { icon: Loader2, color: 'text-blue-500', label: 'Vérification...', bgColor: 'bg-blue-500/10' };
      case 'offline_mode':
        return { icon: ShieldAlert, color: 'text-gray-500', label: 'Mode hors ligne', bgColor: 'bg-gray-500/10' };
      default:
        return { icon: ShieldCheck, color: 'text-gray-500', label: 'Licence', bgColor: 'bg-gray-500/10' };
    }
  };

  const syncConfig = getSyncConfig();
  const licenseConfig = getLicenseConfig();

  // Get days left text
  const getDaysLeftText = () => {
    if (daysUntilExpiry === null) return '';
    if (daysUntilExpiry <= 0) return ' - Expiré';
    if (daysUntilExpiry === 1) return ' - 1 jour restant';
    if (daysUntilExpiry <= 7) return ` - ${daysUntilExpiry} jours restants`;
    return '';
  };

  return (
    <div className="flex items-center gap-2">
      {/* Network Status */}
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5">
            <span className={cn("relative flex h-2 w-2", isOnline && "animate-pulse")}>
              <span className={cn("absolute inline-flex h-full w-full rounded-full opacity-75", networkConfig.bgColor)} />
              <span className={cn("relative inline-flex rounded-full h-2 w-2", networkConfig.bgColor)} />
            </span>
            <Badge variant="secondary" className="text-xs font-medium px-2 py-0.5">
              <networkConfig.icon className={cn("w-3 h-3 mr-1", networkConfig.color)} />
              {networkConfig.label}
            </Badge>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{isOnline ? 'Connecté à Internet' : 'Pas de connexion Internet'}</p>
        </TooltipContent>
      </Tooltip>

      <Separator orientation="vertical" className="h-4" />

      {/* Sync Status */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="secondary" className="text-xs font-medium px-2 py-0.5">
            <syncConfig.icon className={cn(
              "w-3 h-3 mr-1",
              syncConfig.color,
              syncConfig.animate && "animate-spin"
            )} />
            {syncConfig.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {syncState === 'syncing' && 'Synchronisation des données en cours...'}
            {syncState === 'synced' && 'Toutes les données sont synchronisées'}
            {syncState === 'pending' && 'Des données attendent d\'être synchronisées'}
            {syncState === 'error' && 'Erreur lors de la synchronisation'}
          </p>
        </TooltipContent>
      </Tooltip>

      <Separator orientation="vertical" className="h-4" />

      {/* License Status - Clickable */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Link to="/settings/subscription">
            <Badge 
              variant="secondary" 
              className={cn(
                "text-xs font-medium px-2 py-0.5 cursor-pointer hover:bg-accent transition-colors",
                licenseConfig.bgColor
              )}
            >
              <licenseConfig.icon className={cn(
                "w-3 h-3 mr-1",
                licenseConfig.color,
                licenseState === 'verifying' && "animate-spin"
              )} />
              {licenseConfig.label}
              {daysUntilExpiry !== null && daysUntilExpiry <= 7 && daysUntilExpiry > 0 && (
                <span className="ml-1 text-amber-600 font-bold">({daysUntilExpiry}j)</span>
              )}
            </Badge>
          </Link>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-sm">
            <p className="font-medium">
              {licenseState === 'valid' && '✓ Licence valide'}
              {licenseState === 'verification_needed' && '⚠ Vérification requise'}
              {licenseState === 'expired' && '✗ Licence expirée'}
              {licenseState === 'verifying' && '⟳ Vérification en cours...'}
              {licenseState === 'offline_mode' && '⚡ Mode hors ligne'}
            </p>
            {getDaysLeftText() && (
              <p className="text-xs text-muted-foreground mt-1">{getDaysLeftText()}</p>
            )}
            <p className="text-xs text-blue-500 mt-1">Cliquez pour gérer l&apos;abonnement</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

export default StatusIndicators;
